import facebookpixel from "@shared/lib/analytics/facebookpixel";
import gtag from "@shared/lib/analytics/gtag";
import gtm from "@shared/lib/analytics/gtm";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import { AuthProfileSerializer } from "@shared/modules/auth/types";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import {
  AnalyticEventTypes,
  GtagBasketType,
  GtagItemGroupType,
  ContentViewItem,
  GtagTicketType,
} from "@shared/lib/analytics/types/analytics-types";
import { queryClient } from "@shared/services/api/utils/GlobalQueryClient";
import { IHasAnalyticsParams } from "../AnalyticsService-types";

/*
 * This is a dumb class that just multiplexes calls to the various analytics platforms.
 */
export class ExternalAnalytics {
  private authRepo = new AuthProfileRepository();
  private keyManager = new QueryKeyManager(this.authRepo);
  private key = this.keyManager.generateKey(["retrieve"]);

  init = (venue: IHasAnalyticsParams) => {
    if (venue?.org_facebook_pixel) {
      const userProfile = queryClient.getQueryData<
        AuthProfileSerializer | undefined
      >(this.key);
      facebookpixel.init(venue.org_facebook_pixel, userProfile);
    }

    if (venue.aw_global_tag) {
      gtag.init(venue.aw_global_tag);
    }

    if (venue.ga_4_tracking_id) {
      gtag.init(venue.ga_4_tracking_id);
    }

    if (venue.google_tag_manager_id) {
      gtm.init(venue.google_tag_manager_id);
    }
  };

  disableVenue = (venue: IHasAnalyticsParams) => {
    if (venue.org_facebook_pixel) {
      facebookpixel.revokeConsent(); // Best approximation for disabling Facebook Pixel
    }

    if (venue.aw_global_tag) {
      gtag.disable(venue.aw_global_tag);
    }

    if (venue.ga_4_tracking_id) {
      gtag.disable(venue.ga_4_tracking_id);
    }

    if (venue.google_tag_manager_id) {
      gtm.disable(venue.google_tag_manager_id);
    }
  };

  disableAllAnalytics = () => {
    facebookpixel.revokeConsent();
    gtag.disableAll();
  };

  pageView = (uuid?: string) => {
    facebookpixel.pageView(uuid ? { uuid } : undefined);
  };

  viewContent = (item: ContentViewItem, uuid?: string) => {
    facebookpixel.viewContent(item, uuid ? { uuid } : undefined);
  };

  viewItems = (
    ttset: Array<GtagTicketType> | GtagBasketType,
    venue: NestedPublicVenueSerializer | PublicVenueSerializer
  ) => {
    gtag.viewItems(ttset, venue);
    gtm.viewItems(ttset, venue);
  };

  addToCart = (
    formattedItemGroup: GtagItemGroupType,
    quantity: number,
    venue: NestedPublicVenueSerializer | PublicVenueSerializer,
    opts: { uuid: string }
  ) => {
    facebookpixel.addToCart(formattedItemGroup, quantity, venue, opts);
    gtag.addToCart(formattedItemGroup, quantity, venue, opts);
    gtm.addToCart(formattedItemGroup, quantity, venue, opts);
  };

  removeFromCart = () => {
    gtag.removeFromCart();
    gtm.removeFromCart();
  };

  beginCheckout = (
    basket: GtagBasketType,
    venue?: NestedPublicVenueSerializer,
    uuid?: string
  ) => {
    facebookpixel.beginCheckout(basket, venue, uuid ? { uuid } : undefined);
    gtag.beginCheckout(basket, venue);
    gtm.beginCheckout(basket, venue);
  };

  postPurchase = (
    basket: GtagBasketType,
    venue?: NestedPublicVenueSerializer,
    uuid?: string
  ) => {
    facebookpixel.postPurchase(basket, venue, uuid ? { uuid } : undefined);
    gtag.postPurchase(basket, venue);
    gtm.postPurchase(basket, venue);
  };

  awConversion = (
    conversionID: string,
    conversionLabel: string,
    item: { value: number; currency: string; transaction_id: string },
    ...args: unknown[]
  ) => {
    gtag.awConversion(conversionID, conversionLabel, item, ...args);
    gtm.awConversion(conversionID, conversionLabel, item, ...args);
  };

  track = (...args: Parameters<AnalyticEventTypes["track"]>) => {
    facebookpixel.track(...args);
    gtag.track(...args);
    gtm.track(...args);
  };

  grantConsent = () => {
    facebookpixel.grantConsent();
    gtag.grantConsent();
    gtm.grantConsent();
  };

  revokeConsent = () => {
    facebookpixel.revokeConsent();
    gtag.revokeConsent();
    gtm.revokeConsent();
  };
}
